import get from 'lodash-es/get'
import type { Reducer } from 'redux'

const defaultParse = (v: unknown) => v || {}

type AsyncTypes = {
  pending?: string
  fulfilled?: string
  rejected?: string
  reset?: string
}

// Redux 5 removed AnyAction. This local equivalent keeps reducer handlers
// working without requiring every action to be fully typed. It matches Redux's
// own `UnknownAction`, which is what `Reducer` hands to us.
type LegacyAction = { type: string; [key: string]: unknown }

/**
 * `Payload` is the shape the caller's handlers expect on the action. It can't be
 * derived from the action itself (which is only known to carry *some* payload),
 * so callers declare it and `asyncReducer` asserts it at the handler boundary.
 */
type AsyncReducerOpts<State, Payload> = {
  types: AsyncTypes
  onPending?: (payload: Payload, state: State) => Partial<State>
  parsePayload?: (payload: Payload, state: State) => Partial<State>
  parseError?: (action: LegacyAction, state: State) => Partial<State>
  parseReset?: (payload: Payload, state: State) => Partial<State>
}

type MapOfFunctions<State> = {
  [index: string]: (state: State, action: LegacyAction) => State
}

export type AsyncReducerError = {
  message?: string
  code?: number | null
}

export type AsyncReducerState = {
  error: AsyncReducerError | null
  loading: boolean
  success: boolean | null
}

export const INITIAL_ASYNC_REDUCER_STATE = {
  error: null,
  loading: false,
  success: null,
}

export const asyncReducer = <
  State extends AsyncReducerState,
  Payload = unknown,
>({
  types,
  onPending = defaultParse,
  parsePayload = defaultParse,
  parseError = defaultParse,
  parseReset = defaultParse,
}: AsyncReducerOpts<State, Payload>): MapOfFunctions<State> => {
  const reducer: MapOfFunctions<State> = {}

  if (types.pending) {
    reducer[types.pending] = (state, { payload }) => ({
      ...state,
      error: null,
      loading: true,
      ...onPending(payload as Payload, state),
    })
  }

  if (types.fulfilled) {
    reducer[types.fulfilled] = (state, { payload }) => ({
      ...state,
      ...parsePayload(payload as Payload, state),
      error: null,
      loading: false,
      success: true,
    })
  }

  if (types.rejected) {
    reducer[types.rejected] = (state, action) => ({
      ...state,
      loading: false,
      error: {
        message: get(
          action,
          'error.response.data.errorMessage',
          action.message
        ) as string | undefined,
        code: get(action, 'error.response.status', null) as number | null,
      },
      ...parseError(action, state),
    })
  }

  if (types.reset) {
    reducer[types.reset] = (state, { payload }) => ({
      ...state,
      loading: false,
      error: null,
      success: null,
      ...parseReset(payload as Payload, state),
    })
  }

  return reducer
}

const makeReducer = <State>(
  initialState: State,
  mapOfFunctions: MapOfFunctions<State>
) => {
  const reducer: Reducer<State> = (state, action) => {
    if (state === undefined) {
      return initialState
    }

    const handler = mapOfFunctions[action.type]
    return handler ? handler(state, action) : state
  }
  return reducer
}
export default makeReducer
